<template>
  <div>
    <div class="lg:flex">
      <div class="flex flex-1 shadow items-center">
        <place-selector :styled="false" :italianOnly="true" placeholder="Cerca località" :country="['ch', 'it']" v-model="searchedPlace" class="flex-1 p-3" />
        <button class="p-3" v-if="searchedPlace" @click="searchedPlace = null">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 15 15">
            <path d="M0,0 L15,15 M15,0 L0,15" stroke="#e3064a" title="Annulla" stroke-linecap="butt"/>
          </svg>
        </button>
      </div>
      <dropdown ref="radiusDropdown" :outerlabel="`${radius}km`" innerlabel="Raggio" class="flex-1" :enabled="!!searchedPlace">
        <a
          class="border-t border-line p-3 block"
          v-for="r of [0, 5, 10, 25, 50, 100]"
          @click="radius = r"
          :key="`r-${r}`"
        >{{r}}km</a>
      </dropdown>
    </div>
    <div class="p-3 py-12">
      <template v-if="places === false">
        <div class="my-4">
          <div class="text-center">
            caricamento...
          </div>
        </div>
      </template>
      <template v-else-if="places.length">
        <div
          v-for="place in places"
          :key="place.grid"
          class="my-4"
        >
          <div>
            {{place.name}} <template v-if="radius && searchedPlace">+ {{radius}}km</template> ({{place.total}} {{place.total==1?'utente':'utenti'}})
          </div>
          <div class="flex gap-1">
            <div 
              v-for="([count, option], i) of place.distribution"
              :key="option.id"
              :style="{width: `${100*count/place.total}%`, backgroundColor: colors[i]}"
              :title="`${option.text}: ${count} ${count==1?'risposta':'risposte'} (${Math.round(100*count/place.total)}%)`"
              class="h-2 flex-shrink"
              :class="{'hidden': count===0 }"
              >
            </div>
          </div>
          
        </div>
      </template>
      <template v-else>
        <div class="my-4">
          <div class="text-center">
            Non ci sono ancora sufficienti dati da visualizzare<br />
            <template v-if="searchedPlace">
              Prova ad aumentare il raggio di ricerca
            </template>
          </div>
        </div>
      </template>
    </div>
    
    <card title="Legenda" type="square" class="rounded-b-lg" v-if="stats">
      <div class="flex p-3 flex-wrap gap-8">
        <div
          v-for="(option, i) in stats.options"
          :key="option.id"
          class="flex flex-1"
        >
          <div class="h-6 w-6 mr-4" :style="{backgroundColor: colors[i]}"></div>
          <div class="whitespace-no-wrap">{{option.text}}</div>
        </div>
      </div>
    </card>
  </div>
</template>

<script>
/**
 * @typedef {import('@/services/api').QuestionStats} QuestionStats
 */

import { gridToCoords } from '@/services/mapdata'
import PlaceSelector from '../PlaceSelector.vue'
import { reverseLookup } from '@/services/places'
import colors from '@/services/colors'

function deg2rad (deg) {
  return deg * (Math.PI / 180)
}
function distanceInKm({lat: lat1, lon: lon1}, {lat: lat2, lon: lon2}) {
  const R = 6371 // Radius of the earth in km
  const dLat = deg2rad(lat2 - lat1)
  const dLon = deg2rad(lon2 - lon1)
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2)
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  const d = R * c // Distance in km
  return d
}

export default {
  components: { PlaceSelector },
  props: {
    /** @type {QuestionStats} */
    stats: { 
      type: Object,
      required: true
    },
  },
  data () {
    return {
      searchedPlace: null,
      radius: 0,
      colors: colors,
    }
  },
  watch: {
    radius: function () {
      this.$refs.radiusDropdown.close();
    }
  },
  computed: {
    searchedCoords() {
      if (this.searchedPlace) {
        return gridToCoords(this.searchedPlace.grid)
      }
      return undefined
    },
  },
  asyncComputed: {
    places: { 
      async get() {
        const use = this.stats.use;
        const options = this.stats.options;

        let places = [];

        if(this.searchedPlace) {
          if(this.radius) {
            let total = 0;
            const orderedDistribution = [];

            for(let i = 0; i < options.length; i++) {
              const option = options[i];
              const usedata = use[option.id] || {};

              for(const [grid, count] of Object.entries(usedata)) {
                const coords = gridToCoords(grid);
                const dist = distanceInKm(this.searchedCoords, coords);
                if(dist <= this.radius) {
                  total += count;
                  orderedDistribution[i] = (orderedDistribution[i] || 0) + count;
                }
              }
            }
            places = [
              {
                name: this.searchedPlace.name,
                total: total,
                distribution: orderedDistribution.map((count, i) => [count, options[i]])
              }
            ];

          } else {
            let total = 0;
            const orderedDistribution = [];
            for(let i = 0; i < options.length; i++) {
              const option = options[i];
              const usedata = use[option.id] || {};

              if(usedata[this.searchedPlace.grid]) {
                const count = usedata[this.searchedPlace.grid];
                total += count;
                orderedDistribution[i] = count;
              } else {
                orderedDistribution[i] = 0;
              }
            }
            places = [{
              name: this.searchedPlace.name,
              total: total,
              distribution: orderedDistribution.map((count, i) => [count, options[i]])
            }];
          }
        } else {

          // top 5 grids for all options
          const gridSums = Object.values(use).reduce((acc, optionData) => {
            for(const [grid, count] of Object.entries(optionData)) {
              acc[grid] = (acc[grid] || 0) + count
            }
            return acc
          }, {});

          places = await Promise.all(Object.entries(gridSums)
            .sort(([, aCount], [, bCount]) => bCount - aCount)
            .slice(0, 5)
            .map(async ([grid, total]) => 
              ({
                name: (await reverseLookup(parseInt(grid))).name,
                total,
                distribution: options.map(option => {
                  if(use[option.id] && use[option.id][grid]) {
                    return [use[option.id][grid], option];
                  } else {
                    return [0, option];
                  }
                })
              })
            )
          );
        }

        return places.filter(p => p.total > 0);

      },
      watch: ['stats', 'searchedPlace', 'radius'],
      default: false
    }
  }
}
</script>

<style>

</style>